import React from 'react'
import { connect } from 'react-redux'
import { TidewaysContext } from '../app/context'
import { Entity } from '../app/entity'
import Navigator from '../app/navigator'
import { MonitorOperation } from '../domain/Operation'
import BoxBody from '../helper/boxBody'
import Link from '../helper/link'
import Loading from '../helper/loading'
import { ApplicationState } from '../redux/ApplicationState'
import { FetchTraceHistoryParameters } from '../redux/V2/Trace/actions'
import { fetchActiveTracepoints } from '../redux/V2/TracepointRule/actions'
import { parseTransactionName } from '../ui/transactionName'
import TransactionTableRowItem from './transaction-table-row-item'
import SortableColumnHeader from './operation/sortable-column-header'

interface StateProps {
    tidewaysContext: TidewaysContext
    parameters: { [key: string]: string }
    navigator: Navigator
}

interface DispatchProps {
    fetchActiveTracepoints: ({
        environment,
        service,
    }: FetchTraceHistoryParameters) => { type: string; payload: ({ fetchForApplication }: { fetchForApplication: any }) => any }
}

type Props = StateProps &
    DispatchProps &
    Entity<{ critical: { operations: MonitorOperation[] }; ignored_transactions_count: number } | null>

export class CriticalTransactionsScene extends React.Component<Props> {
    public static defaultProps = {
        loading: false,
        data: null,
    }

    public componentDidMount() {
        const { service, environment } = this.props.tidewaysContext

        // fetch tracepoints for the whole list only once
        // it's actually used in <Transaction/> -> <TracepointIndicator/>
        this.props.fetchActiveTracepoints({ service, environment })
    }

    public render() {
        const count = parseInt(this.props.parameters.count, 10) || 20
        const sort = this.props.parameters.sort || 'impact'

        if (!this.props.data?.critical?.operations) {
            return <Loading {...this.props} height={100} title="Transactions" />
        }

        const untaggedTransactions = this.props.data.critical.operations.filter(
            transaction => parseTransactionName(transaction.identifier).tag === undefined,
        )

        const taggedTransactions = {}
        this.props.data.critical.operations
            .filter(transaction => parseTransactionName(transaction.identifier).tag !== undefined)
            .forEach(transaction => {
                const { name, tag } = parseTransactionName(transaction.identifier)

                if (!(name in taggedTransactions)) {
                    taggedTransactions[name] = {}
                }

                taggedTransactions[name][tag] = transaction
            })

        return (
            <div className="box">
                <div className="box-header">
                    <nav className="navbar navbar-toolbar">
                        <div className="container-fluid">
                            <h3 className="box-title">Response Times by Transaction</h3>

                            <form
                                className="navbar-form navbar-right"
                                onSubmit={event => {
                                    event.preventDefault()
                                    event.stopPropagation()

                                    this.props.navigator.replace({ search: jQuery('.operation-list-search').val() })
                                }}
                            >
                                <div className="form-group has-feedback operation-list-group">
                                    <input
                                        className="form-control operation-list-search"
                                        defaultValue={this.props.parameters.search}
                                        type="text"
                                    />
                                    <i className="form-control-feedback fa fa-search" />
                                </div>
                            </form>
                        </div>
                    </nav>
                </div>

                <BoxBody {...this.props} className="no-padding">
                    <table className="table table-striped profile table-fixed">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th style={{ width: 100 }} className="align-right">
                                    <SortableColumnHeader label={'Requests'} sortKey={'requests'} currentSortKey={sort} />
                                </th>
                                <th style={{ width: 170 }} className="align-right">
                                    <SortableColumnHeader label={'Average Response'} sortKey={'average'} currentSortKey={sort} />
                                </th>
                                <th style={{ width: 160 }} className="align-right">
                                    <SortableColumnHeader label={'Slowest Response'} sortKey={'max'} currentSortKey={sort} />
                                </th>
                                <th style={{ width: 110 }} className="align-right">
                                    <SortableColumnHeader label={'Memory'} sortKey={'memory'} currentSortKey={sort} />
                                </th>
                                <th style={{ width: 110 }} className="align-right">
                                    <SortableColumnHeader label={'Failures'} sortKey={'errors'} currentSortKey={sort} />
                                </th>
                                <th style={{ width: 110 }} className="align-right">
                                    <SortableColumnHeader label={'Impact'} sortKey={'impact'} currentSortKey={sort} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {untaggedTransactions.length > 0 ? (
                                untaggedTransactions.map(operation => (
                                    <TransactionTableRowItem
                                        full
                                        key={operation.id}
                                        operation={operation}
                                        tagged={taggedTransactions[operation.identifier]}
                                        isTideways6={this.props.tidewaysContext.organization.isTideways6}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} align="center">
                                        No requests recorded in selected period.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            {this.props.data.ignored_transactions_count > 0 && (
                                <tr className="tw-font-size-16">
                                    <td colSpan={7} className="text-left">
                                        <span>
                                            In addition{' '}
                                            <Link
                                                route="XhprofProfilerBundle.Transaction.list"
                                                params={{ 'criteria[visibility]': 'Ignored' }}
                                                className={{ display: 'inline' }}
                                            >
                                                {this.props.data.ignored_transactions_count} transactions
                                            </Link>{' '}
                                            with ignored visibility sent data during the selected time period.
                                        </span>
                                    </td>
                                </tr>
                            )}
                            {count > this.props.data.critical.operations.length ? null : (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        <Link params={{ ...this.props.parameters, count: (+this.props.parameters.count || 20) + 20 }}>
                                            Fetch more…
                                        </Link>
                                    </td>
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </BoxBody>
            </div>
        )
    }
}

export default connect<StateProps & Entity<any>, DispatchProps, {}>(
    (globalState: ApplicationState, props) => {
        const operations = globalState.monitor.operations || new Entity()

        return {
            navigator: new Navigator(globalState.tidewaysContext.route),
            tidewaysContext: globalState.tidewaysContext,
            parameters: globalState.tidewaysContext.route.parameters,
            ...operations,
        }
    },
    {
        fetchActiveTracepoints,
    },
)(CriticalTransactionsScene)
